// Block K (UI-20, 2026-10-11): the Admin panel on the v2 kit, from mockups/admin-panel.html.
// Users (plan chips, search, the list, a drawer per user), Activity log (grouped by day, labels
// from Admin/adminLabels.js), Requests (approve cards), Ownership (super_admin: steps, safety
// checks, TRANSFER typed). Pieces: Admin/AdminUserPanel.jsx, Admin/AdminAddUser.jsx,
// Admin/OwnershipTransfer.jsx, Admin/admin.css.
//
// The gates are exactly as they were: App.js lets super_admin, admin and cs_manager reach this
// component (AdminPanelRoute below holds that same list, so App.js now says no instead of drawing
// a blank page); inside, ALLOWED_ROLES (super_admin, cs_manager, cs_agent) see the panel; Add
// user and the role field are super_admin only; Remove and the personal plan are super_admin and
// cs_manager; Ownership is super_admin. The server enforces its own rules on every route.
//
// Defects fixed, each failing first in Admin/AdminPanel.test.js: a blank page for anyone else;
// every failed load drawn as an empty list ("No users found.", "No activity logged yet.", "No
// pending requests.", "No transfer jobs."); a refused transfer read "Request failed with status
// 409"; Remove used window.prompt. Dead `if (!res.ok)` checks after api() are gone (it throws).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { messageFromError } from '../utils/apiError';
import { formatDate, formatDateTime } from '../utils/formatting';
import { isAdminRole } from '../nav/views';
import {
  Avatar, Banner, Button, Card, ChipGroup, EmptyState, ErrorState, Field, Input, List, ListRow, PageHeader, PageLayout,
  RowPrimary, SearchInput, Skeleton, StatusPill, TabGroup, TabPanel, HeaderButton,
} from '../ui';
import AdminUserPanel, { LogLine } from './Admin/AdminUserPanel';
import AdminAddUser from './Admin/AdminAddUser';
import OwnershipTransfer from './Admin/OwnershipTransfer';
import { actionLook, groupByDay, planTone, programmeTerms, roleLabel, statusTone, userTypeLabel } from './Admin/adminLabels';
import './Admin/admin.css';

const ALLOWED_ROLES = ['super_admin', 'cs_manager', 'cs_agent'];
const EMPTY_META = { plans: [], editablePlans: [], accountStatuses: [], customerProgramTypes: [], createUserRoles: [], programmeTypes: [], csManagerGrantable: [] };
const arr = (x) => (Array.isArray(x) ? x : []);

function Refusal({ text, testId = 'admin-refused' }) {
  return (
    <PageLayout data-page="admin">
      <PageHeader eyebrow={{ label: 'Admin', color: 'pastel-periwinkle' }} title="Admin panel" />
      <div data-testid={testId}>
        <EmptyState icon="🔒" title="This page is for Aidey staff">{text}</EmptyState>
      </div>
    </PageLayout>
  );
}

// App.js's gate for /admin, unchanged (isAdminRole: super_admin, admin, cs_manager). Anyone else
// used to get a blank page; they are now told, and nothing is fetched.
export function AdminPanelRoute({ currentUser }) {
  if (!currentUser) return null;
  if (!isAdminRole(currentUser.role)) return <Refusal text="Your account does not have access to the Admin panel." />;
  return <AdminPanel />;
}

export default function AdminPanel() {
  const { currentUser } = useCurrentUser();
  const role = String(currentUser?.role || '').toLowerCase();
  if (!ALLOWED_ROLES.includes(role)) {
    return <Refusal testId="admin-role-refused" text="This panel requires Super Admin, CS Manager, or CS Agent access." />;
  }
  return <AdminPanelInner currentUser={currentUser} role={role} />;
}

const TAB_IDS = { users: 'users', worklog: 'worklog', requests: 'requests', ownership: 'ownership' };

function AdminPanelInner({ currentUser, role }) {
  const [tab, setTab] = useState(TAB_IDS.users);
  const [users, setUsers] = useState({ status: 'loading', list: [], error: '' });
  const [metadata, setMetadata] = useState(EMPTY_META);
  const [logs, setLogs] = useState({ status: 'idle', list: [], error: '' });
  const [requests, setRequests] = useState({ status: 'loading', list: [], error: '' });
  const [planFilter, setPlanFilter] = useState('');
  const [query, setQuery] = useState('');
  const [logType, setLogType] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [notice, setNotice] = useState(null);   // { tone, text }
  const [resetFallbackUrl, setResetFallbackUrl] = useState('');
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [adding, setAdding] = useState(false);
  const [review, setReview] = useState({});     // requestId -> { mode, approvedDays, reason, error, busy }

  const grantableProgrammes = role === 'super_admin' ? metadata.programmeTypes : metadata.csManagerGrantable;

  const loadUsers = useCallback(async () => {
    setUsers((u) => ({ ...u, status: u.list.length ? u.status : 'loading' }));
    try {
      const [uRes, mRes] = await Promise.all([api('/api/users'), api('/api/aidey-team/metadata')]);
      const [uData, mData] = await Promise.all([uRes.json(), mRes.json()]);
      setUsers({ status: 'ready', list: arr(uData?.users), error: '' });
      setMetadata(Object.fromEntries(Object.keys(EMPTY_META).map((k) => [k, arr(mData?.[k])])));
    } catch (err) {
      setUsers((u) => ({ ...u, status: 'failed', error: messageFromError(err, 'The users could not be loaded.') }));
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLogs((l) => ({ ...l, status: 'loading' }));
    try {
      const res = await api('/api/aidey-team/work-log?limit=200');
      const data = await res.json();
      setLogs({ status: 'ready', list: arr(data?.logs), error: '' });
    } catch (err) {
      setLogs((l) => ({ ...l, status: 'failed', error: messageFromError(err, 'The activity log could not be loaded.') }));
    }
  }, []);

  const loadRequests = useCallback(async () => {
    try {
      const res = await api('/api/aidey-team/work-requests?status=pending');
      const data = await res.json();
      setRequests({ status: 'ready', list: arr(data?.requests), error: '' });
    } catch (err) {
      setRequests((r) => ({ ...r, status: 'failed', error: messageFromError(err, 'The requests could not be loaded.') }));
    }
  }, []);

  useEffect(() => { loadUsers(); loadRequests(); }, [loadUsers, loadRequests]);
  useEffect(() => { if (tab === TAB_IDS.worklog && logs.status === 'idle') loadLogs(); }, [tab, logs.status, loadLogs]);

  const selectedUser = useMemo(() => users.list.find((u) => u.id === selectedUserId) || null, [users.list, selectedUserId]);
  const plans = metadata.plans.length ? metadata.plans : ['Trial', 'Basics', 'Pro', 'Team', 'Enterprise', 'Personal', 'Family'];
  const planOf = (u) => String(u.effectivePlan || u.plan || '').toLowerCase();
  const q = query.trim().toLowerCase();
  const shownUsers = users.list
    .filter((u) => !planFilter || planOf(u).includes(planFilter.toLowerCase()))
    .filter((u) => !q || [u.name, u.email, u.role, u.effectivePlan, u.plan, u.status, u.userType].some((v) => String(v || '').toLowerCase().includes(q)));
  const pendingCount = requests.list.length;

  function openUser(id) { if (!id) return; setSelectedUserId(id); setTab(TAB_IDS.users); }

  const tabs = [
    { id: TAB_IDS.users, label: 'Users', color: 'pastel-blue', count: users.status === 'ready' ? users.list.length : undefined },
    { id: TAB_IDS.worklog, label: 'Activity log', color: 'pastel-periwinkle' },
    { id: TAB_IDS.requests, label: 'Requests', color: 'pastel-yellow', count: pendingCount || undefined },
    ...(role === 'super_admin' ? [{ id: TAB_IDS.ownership, label: 'Ownership', color: 'pastel-coral' }] : []),
  ];

  const subline = users.status === 'ready'
    ? `${users.list.length} user${users.list.length === 1 ? '' : 's'} · ${requests.status === 'failed' ? 'requests did not load' : pendingCount ? `${pendingCount} request${pendingCount === 1 ? '' : 's'} waiting` : 'no requests waiting'}`
    : undefined;

  return (
    <PageLayout data-page="admin">
      <PageHeader
        eyebrow={{ label: `Admin · ${currentUser?.name || 'You'} · ${roleLabel(role)}`, color: 'pastel-periwinkle' }}
        title="Admin panel"
        subline={subline}
        primary={role === 'super_admin' ? <HeaderButton primary onClick={() => { setNewUserCredentials(null); setAdding(true); }}>+ Add user</HeaderButton> : undefined}
      />

      {notice && <Banner tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Banner>}
      {resetFallbackUrl && (
        <Banner tone="info" onDismiss={() => setResetFallbackUrl('')}>
          <span className="adm-stack adm-stack--tight">
            <strong>Fallback reset link (the email was not delivered)</strong>
            <span>Send this link to the user yourself so they can reset their password now. It expires in 1 hour.</span>
            <span className="adm-copy">
              <Input readOnly value={resetFallbackUrl} onFocus={(e) => e.target.select()} aria-label="Fallback reset link" className="adm-mono" />
              <Button size="sm" variant="secondary" onClick={() => navigator.clipboard?.writeText(resetFallbackUrl)}>Copy</Button>
            </span>
          </span>
        </Banner>
      )}
      {newUserCredentials && (
        <Banner tone="notice" onDismiss={() => setNewUserCredentials(null)}>
          A temporary password was made for {newUserCredentials.email}: <strong className="adm-mono">{newUserCredentials.temporaryPassword}</strong>
        </Banner>
      )}

      <TabGroup label="Admin sections" idPrefix="adm" tabs={tabs} value={tab} onChange={(id) => { setTab(id); setSelectedUserId(null); }} />

      <TabPanel idPrefix="adm" tabId={tab}>
        {tab === TAB_IDS.users && (
          users.status === 'loading' ? <Skeleton lines={6} label="Loading users" />
            : users.status === 'failed' ? <ErrorState title="The users did not load" message={users.error} onRetry={loadUsers} />
            : (
              <div className="adm-stack">
                <div className="adm-toolbar">
                  <ChipGroup label="Plan" value={planFilter} onChange={setPlanFilter} scroll
                    options={[{ value: '', label: `All ${users.list.length}` }, ...plans.map((p) => ({ value: p, label: `${p} ${users.list.filter((u) => planOf(u).includes(p.toLowerCase())).length}` }))]} />
                  <SearchInput label="Search users" placeholder="Search name, email, status…" value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>
                {shownUsers.length === 0 ? (
                  <EmptyState icon="🔍" title={users.list.length ? 'No users match' : 'No users yet'}
                    action={(q || planFilter) ? <Button variant="tertiary" onClick={() => { setQuery(''); setPlanFilter(''); }}>Clear the search</Button> : null}>
                    {users.list.length ? 'Try another name or plan.' : 'Accounts appear here as people sign up.'}
                  </EmptyState>
                ) : (
                  <List label="Users" columns={[
                    { key: 'user', label: 'User', role: 'primary', width: 'minmax(0, 2.2fr)' },
                    { key: 'plan', label: 'Plan', width: 'minmax(0, 1fr)' },
                    { key: 'status', label: 'Status', role: 'status', width: 'minmax(0, 0.9fr)' },
                    { key: 'type', label: 'User type', width: 'minmax(0, 1fr)' },
                    { key: 'terms', label: 'Terms', width: 'minmax(0, 1.2fr)' },
                    { key: 'actions', label: '', width: 'auto' },
                  ]}>
                    {shownUsers.map((u) => (
                      <ListRow key={u.id} onOpen={() => openUser(u.id)} openLabel={`Manage ${u.name || u.email}`}
                        meta={`${u.effectivePlan || u.plan || 'Trial'} · ${programmeTerms(u, (d) => formatDate(d))}`}
                        cells={{
                          user: <RowPrimary leading={<Avatar name={u.name || u.email} size={36} color="pastel-blue" />} name={u.name || 'Unnamed'} sub={<span className="adm-email" title={u.email}>{u.email}</span>} />,
                          plan: <StatusPill tone={planTone(u.effectivePlan || u.plan)}>{u.effectivePlan || u.plan || 'Trial'}</StatusPill>,
                          status: <StatusPill tone={statusTone(u.status)}>{u.status || 'unknown'}</StatusPill>,
                          type: <span className="adm-muted">{userTypeLabel(u.userType) || '–'}</span>,
                          terms: <span className="adm-muted">{programmeTerms(u, (d) => formatDate(d))}</span>,
                          actions: <Button size="sm" variant="secondary" onClick={() => openUser(u.id)} aria-label={`Manage ${u.name || u.email}`}>Manage</Button>,
                        }} />
                    ))}
                  </List>
                )}
              </div>
            )
        )}

        {tab === TAB_IDS.worklog && (
          <div className="adm-stack">
            <div className="adm-toolbar">
              <ChipGroup label="Show" value={logType} onChange={setLogType} scroll
                options={[{ value: 'all', label: 'Everything' }, ...[...new Set(logs.list.map((l) => l.action))].map((a) => ({ value: a, label: actionLook(a).label }))]} />
              <Button variant="tertiary" onClick={loadLogs} disabled={logs.status === 'loading'} aria-busy={logs.status === 'loading' || undefined}>{logs.status === 'loading' ? 'Refreshing…' : 'Refresh'}</Button>
            </div>
            {logs.status === 'loading' || logs.status === 'idle' ? <Skeleton lines={5} label="Loading the activity log" />
              : logs.status === 'failed' ? <ErrorState title="The activity log did not load" message={logs.error} onRetry={loadLogs} />
              : logs.list.length === 0 ? <EmptyState icon="🗒️" title="Nothing has been logged yet">Account changes and trial requests appear here as they happen.</EmptyState>
              : groupByDay(logs.list.filter((l) => logType === 'all' || l.action === logType), (d) => formatDate(d, undefined, { weekday: 'short' })).map((g) => (
                <section key={g.key} className="adm-day" aria-label={g.label}>
                  <div className="adm-day__head"><h2 className="adm-day__title">{g.label}</h2><span className="adm-muted">{g.items.length} change{g.items.length === 1 ? '' : 's'}</span></div>
                  <Card lift={false} className="adm-day__card"><ul className="adm-logs">{g.items.map((log) => <LogLine key={log.id} log={log} onOpenUser={openUser} />)}</ul></Card>
                </section>
              ))}
          </div>
        )}

        {tab === TAB_IDS.requests && (
          <div className="adm-stack">
            <p className="adm-muted">Trial extension requests. Approving adds days to the account's trial.</p>
            {requests.status === 'loading' ? <Skeleton lines={4} label="Loading requests" />
              : requests.status === 'failed' ? <ErrorState title="The requests did not load" message={requests.error} onRetry={loadRequests} />
              : requests.list.length === 0 ? <EmptyState icon="✅" title="All caught up">No requests are waiting.</EmptyState>
              : (
                <div className="adm-cards">
                  {requests.list.map((r) => (
                    <RequestCard key={r.id} r={r} rv={review[r.id]} setRv={(patch) => setReview((p) => ({ ...p, [r.id]: patch === null ? undefined : { ...(p[r.id] || {}), ...patch } }))}
                      onOpenUser={openUser}
                      onDone={async (mode) => { setNotice({ tone: 'success', text: mode === 'approve' ? 'Request approved and logged.' : 'Request declined and logged.' }); await Promise.all([loadRequests(), loadUsers()]); }} />
                  ))}
                </div>
              )}
          </div>
        )}

        {tab === TAB_IDS.ownership && role === 'super_admin' && <OwnershipTransfer />}
      </TabPanel>

      {selectedUser && (
        <AdminUserPanel user={selectedUser} role={role} metadata={metadata} grantableProgrammes={grantableProgrammes}
          onClose={() => setSelectedUserId(null)} onChanged={loadUsers} onNotice={setNotice} onResetFallback={setResetFallbackUrl} />
      )}

      <AdminAddUser open={adding} onClose={() => setAdding(false)} metadata={metadata} grantableProgrammes={grantableProgrammes}
        onCreated={async (data) => {
          setAdding(false);
          setNotice({ tone: 'success', text: `User ${data.user.email} created.` });
          if (data.temporaryPasswordGenerated && data.temporaryPassword) setNewUserCredentials({ email: data.user.email, temporaryPassword: data.temporaryPassword });
          await loadUsers();
          setSelectedUserId(data.user.id);
        }} />
    </PageLayout>
  );
}

const DAY_CHIPS = [7, 14, 30];

function RequestCard({ r, rv, setRv, onOpenUser, onDone }) {
  const days = r.metadata?.requestedDays;
  async function confirm() {
    if (rv.mode === 'approve') {
      const n = Number(rv.approvedDays);
      if (!Number.isFinite(n) || n < 1) { setRv({ error: 'Approved days must be at least 1.' }); return; }
    }
    setRv({ busy: true, error: '' });
    try {
      const payload = { decision: rv.mode };
      if (rv.mode === 'approve') payload.approvedDays = Number(rv.approvedDays);
      if (rv.reason) payload.reason = rv.reason;
      await api(`/api/aidey-team/work-requests/${r.id}/review`, { method: 'POST', body: JSON.stringify(payload) });
      setRv(null);
      await onDone(rv.mode);
    } catch (err) {
      setRv({ busy: false, error: messageFromError(err, rv.mode === 'approve' ? 'The request was not approved.' : 'The request was not declined.') });
    }
  }
  return (
    <Card color="pastel-yellow" label="Trial extension" meta={r.createdAt ? `Asked ${formatDateTime(r.createdAt)}` : undefined} lift={false}
      footer={!rv ? (
        <>
          <Button variant="tertiary" onClick={() => onOpenUser(r.targetUser?.id)} disabled={!r.targetUser?.id}>View user</Button>
          <Button variant="destructive" onClick={() => setRv({ mode: 'reject', approvedDays: '', reason: '' })}>Decline</Button>
          <Button variant="confirm" onClick={() => setRv({ mode: 'approve', approvedDays: days ? String(days) : '', reason: '' })}>Approve</Button>
        </>
      ) : (
        <>
          <Button variant="tertiary" onClick={() => setRv(null)} disabled={rv.busy}>Cancel</Button>
          <Button variant={rv.mode === 'approve' ? 'confirm' : 'delete'} onClick={confirm} disabled={rv.busy} aria-busy={rv.busy || undefined}>
            {rv.busy ? 'Saving…' : rv.mode === 'approve' ? 'Confirm approval' : 'Confirm decline'}
          </Button>
        </>
      )}>
      <div className="adm-stack adm-stack--tight">
        <div className="adm-request__who">
          <span className="adm-who__name">{r.targetUser?.name || r.targetUser?.email || 'Unknown user'}</span>
          {r.targetUser?.name && <span className="adm-muted adm-wrap">{r.targetUser.email}</span>}
        </div>
        <div className="adm-pills">
          {r.targetUser?.plan && <StatusPill tone={planTone(r.targetUser.plan)}>{r.targetUser.plan}</StatusPill>}
          {r.targetUser?.status && <StatusPill tone={statusTone(r.targetUser.status)}>{r.targetUser.status}</StatusPill>}
        </div>
        <span className="adm-muted">
          Asked for <strong>{days || 'an unstated number of'} day{Number(days) === 1 ? '' : 's'}</strong> by {r.actorUser?.name || r.actorUser?.email || 'someone'}{r.actorUser?.role ? ` (${roleLabel(r.actorUser.role)})` : ''}
        </span>
        {r.reason && <p className="adm-quote">“{r.reason}”</p>}
        {rv && (
          <div className="adm-review">
            {rv.mode === 'approve' && (
              <>
                <span className="adm-chip-label" aria-hidden="true">Extend by</span>
                <ChipGroup label="Extend by" value={DAY_CHIPS.map(String).includes(String(rv.approvedDays)) ? String(rv.approvedDays) : null}
                  onChange={(v) => setRv({ approvedDays: v })} options={DAY_CHIPS.map((n) => ({ value: String(n), label: `${n} days` }))} />
                <Field label="Approved days">
                  <Input type="number" min="1" max="365" inputMode="numeric" value={rv.approvedDays} onChange={(e) => setRv({ approvedDays: e.target.value })} placeholder={`Asked for ${days || '?'}`} />
                </Field>
              </>
            )}
            <Field label="Reason (optional)">
              <Input value={rv.reason || ''} onChange={(e) => setRv({ reason: e.target.value })} placeholder={rv.mode === 'approve' ? 'An approval note' : 'Why it is declined'} />
            </Field>
            {rv.error && <p className="adm-error" role="alert">{rv.error}</p>}
          </div>
        )}
      </div>
    </Card>
  );
}
